<script setup lang="ts">
import { watch } from "vue";

import { useInputChangeFlag } from "@/composables/use-input-changed-flag";
import { TSite } from "@/data/api/kili/sites/types/sites.type";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Skeleton from "primevue/skeleton";

import PilotingSiteCard from "@/components/piloting/piloting-site-card.vue";

interface IProps {
  sites?: Array<TSite>;
  isPending?: boolean;
}

interface IEmits {
  (e: "click:site", site: TSite): void;
  (e: "update:searchSite"): void;
}

const props = defineProps<IProps>();
const { t, k } = useTypedIntl();
const emit = defineEmits<IEmits>();

const searchInput = defineModel<string>("value", {
  required: false,
  default: "",
});

const { hasInputBeenUsed: hasSearchSiteInputBeenUsed } = useInputChangeFlag(searchInput);

function handleSelectSite(site: TSite) {
  emit("click:site", site);
}

watch(hasSearchSiteInputBeenUsed, () => {
  MixpanelService.trackEvent(EMixpanelEvent.PILOTING_SITE_SEARCH_PERFORMED);
});
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div
      class="flex shrink-0 flex-col gap-4"
      data-test-id="piloting-site-card-left-panel-header"
    >
      <h3 v-if="props.sites">
        {{ t(k.global.count.sites({ count: props.sites.length })) }}
      </h3>
      <Skeleton
        v-else
        class="!w-40 !h-8"
      />

      <div class="flex flex-col gap-2">
        <div class="flex gap-2">
          <IconField class="w-full">
            <InputIcon class="pi pi-search" />
            <InputText
              v-model.trim="searchInput"
              data-test-id="piloting-search-site-input"
              :placeholder="t(k.global.searchSite)"
              @update:modelValue="emit('update:searchSite')"
            />
          </IconField>
        </div>
      </div>
    </div>

    <div
      class="-mr-2 flex flex-1 flex-col gap-4 overflow-y-auto pr-2"
      data-test-id="piloting-site-list"
    >
      <template v-if="props.isPending">
        <Skeleton
          v-for="i in 5"
          :key="i"
          style="height: 78px"
        />
      </template>
      <template v-else-if="(props.sites ?? []).length > 0">
        <PilotingSiteCard
          v-for="site in props.sites ?? []"
          :key="site.siteId"
          data-test-id="piloting-site-card"
          :site="site"
          @select="handleSelectSite($event)"
        />
      </template>
      <template v-else>
        <div class="w-full py-8 text-center gap-1 flex flex-col items-center justify-center">
          <h3 class="text-base font-semibold">
            {{ t(k.global.misc.noResult) }}
          </h3>
          <span class="text-text-secondary">{{ t(k.global.misc.noResultSearchHelpText) }}</span>
        </div>
      </template>
    </div>
  </div>
</template>
